import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Flex,
  Text,
  Badge,
  Spinner,
  Grid,
  Tag,
  Image,
  Divider,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import dayjs from "dayjs";
import { queryClient } from "App";
import { convertParamsToString } from "helpers/stringManipulation";
import { patchRequest, useQueryWrapper } from "services/apiHelper";
import axiosInstance from "services/httpService";
import {
  categoryRequest,
  composersRequest,
  languageRequest,
  scriptsRequest,
} from "services/request";
import { queryKeys } from "services/querykeys";
import {
  EDIT_REQUEST_FIELD_LABELS,
  QUERY_KEYS,
  type CategoryType,
  getEditRequestFieldGroups,
  type EditRequestType,
  type LanguageType,
  type ScriptType,
} from "utils/constants";
import { errorToast, successToast } from "utils/hooks";

interface EditRequestDiffModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestId: string | null;
  scriptFallback?: ScriptType | null;
  onSuccess?: () => void;
}

type ScriptDisplayValue = Partial<ScriptType> & {
  scriptComposer?: any;
};

const invalidateEditRequestQueries = () => {
  void queryClient.invalidateQueries(QUERY_KEYS.ADMIN_SCRIPTS);
  void queryClient.invalidateQueries({
    predicate: (query) =>
      typeof query.queryKey === "string" && query.queryKey.startsWith(QUERY_KEYS.EDIT_REQUESTS),
  });
};

const normalizeIds = (ids: any): string[] => {
  if (!Array.isArray(ids)) return [];

  return ids
    .flat()
    .map((id) => (typeof id === "string" ? id : id?.id))
    .filter((id): id is string => Boolean(id));
};

const buildNameMap = (items: any[] = []) =>
  items.reduce<Record<string, string>>((acc, item) => {
    if (item?.id && item?.name) {
      acc[item.id] = item.name;
    }
    return acc;
  }, {});

const renderResolvedIds = (ids: string[], nameMap: Record<string, string>, fallbackLabel: string) =>
  ids.map((id) => nameMap[id] ?? fallbackLabel);

const splitLegacyList = (value: any) =>
  typeof value === "string"
    ? value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

const getNonEmptyString = (value: any) =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

const getObjectName = (value: any) => getNonEmptyString(value?.name);

const getObjectId = (value: any) => getNonEmptyString(value) ?? getNonEmptyString(value?.id);

const firstNonEmpty = (...values: any[]) => values.find((value) => getNonEmptyString(value));

const getLyricsFromMeta = (value: any) => {
  if (typeof value?.lyrics === "string") return value.lyrics;

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed?.lyrics === "string") return parsed.lyrics;
    } catch (_err) {
      return value;
    }
    return value;
  }

  return "";
};

const EditRequestDiffModal = ({
  isOpen,
  onClose,
  requestId,
  scriptFallback,
  onSuccess,
}: EditRequestDiffModalProps) => {
  const [requestDetail, setRequestDetail] = useState<EditRequestType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingFieldKey, setLoadingFieldKey] = useState<string | null>(null);
  const [isBatchApproving, setIsBatchApproving] = useState(false);
  const [isBatchDiscarding, setIsBatchDiscarding] = useState(false);
  const { data: allComposers } = useQueryWrapper(queryKeys.COMPOSERS, composersRequest.COMPOSER, {
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });
  const { data: categoryResponse } = useQueryWrapper(
    queryKeys.CATEGORIES,
    categoryRequest.CATEGORIES,
    {
      refetchInterval: false,
      refetchOnWindowFocus: false,
    },
  );
  const { data: languageResponse } = useQueryWrapper(
    queryKeys.LANGUAGES,
    languageRequest.LANGUAGES,
    {
      refetchInterval: false,
      refetchOnWindowFocus: false,
    },
  );

  const composerNameById = useMemo(() => buildNameMap(allComposers?.data ?? []), [allComposers]);
  const categoryNameById = useMemo(
    () => buildNameMap((categoryResponse?.data ?? []) as CategoryType[]),
    [categoryResponse],
  );
  const languageNameById = useMemo(
    () => buildNameMap((languageResponse?.data ?? []) as LanguageType[]),
    [languageResponse],
  );

  const fetchDetail = async () => {
    if (!requestId) return;
    setIsLoading(true);
    try {
      const url = convertParamsToString(scriptsRequest.EDIT_REQUEST_DETAIL, { id: requestId });
      const response = await axiosInstance.get(url);
      setRequestDetail(response.data?.data ?? response.data);
    } catch (err: any) {
      errorToast(err?.response?.data?.message ?? "Failed to load edit request details");
    } finally {
      setIsLoading(false);
    }
  };

  const refreshDetail = async () => {
    if (!requestId) return;
    const url = convertParamsToString(scriptsRequest.EDIT_REQUEST_DETAIL, { id: requestId });
    const response = await axiosInstance.get(url);
    setRequestDetail(response.data?.data ?? response.data);
  };

  useEffect(() => {
    if (!isOpen || !requestId) {
      setRequestDetail(null);
      return;
    }

    void fetchDetail();
  }, [isOpen, requestId]);

  const handleApproveField = async (groupKey: string) => {
    if (!requestId) return;
    setLoadingFieldKey(`approve_${groupKey}`);
    try {
      const url = convertParamsToString(scriptsRequest.APPROVE_EDIT_REQUEST, { id: requestId });
      await patchRequest({ url, data: { fields: [groupKey] } });
      successToast(`Approved ${EDIT_REQUEST_FIELD_LABELS[groupKey] ?? groupKey} changes.`);
      invalidateEditRequestQueries();
      await refreshDetail();
      onSuccess?.();
    } catch (err: any) {
      errorToast(err?.response?.data?.message ?? `Failed to approve ${groupKey}`);
    } finally {
      setLoadingFieldKey(null);
    }
  };

  const handleDiscardField = async (groupKey: string) => {
    if (!requestId) return;
    setLoadingFieldKey(`discard_${groupKey}`);
    try {
      const url = convertParamsToString(scriptsRequest.DISCARD_EDIT_REQUEST, { id: requestId });
      await patchRequest({ url, data: { fields: [groupKey] } });
      successToast(`Discarded ${EDIT_REQUEST_FIELD_LABELS[groupKey] ?? groupKey} changes.`);
      invalidateEditRequestQueries();
      await refreshDetail();
      onSuccess?.();
    } catch (err: any) {
      errorToast(err?.response?.data?.message ?? `Failed to discard ${groupKey}`);
    } finally {
      setLoadingFieldKey(null);
    }
  };

  const handleApproveAll = async () => {
    if (!requestId) return;
    setIsBatchApproving(true);
    try {
      const url = convertParamsToString(scriptsRequest.APPROVE_EDIT_REQUEST, { id: requestId });
      await patchRequest({ url, data: {} });
      successToast("All pending edit changes approved!");
      invalidateEditRequestQueries();
      await refreshDetail();
      onSuccess?.();
    } catch (err: any) {
      errorToast(err?.response?.data?.message ?? "Failed to approve edit request");
    } finally {
      setIsBatchApproving(false);
    }
  };

  const handleDiscardAll = async () => {
    if (!requestId) return;
    setIsBatchDiscarding(true);
    try {
      const url = convertParamsToString(scriptsRequest.DISCARD_EDIT_REQUEST, { id: requestId });
      await patchRequest({ url, data: {} });
      successToast("All pending edit changes discarded.");
      invalidateEditRequestQueries();
      await refreshDetail();
      onSuccess?.();
    } catch (err: any) {
      errorToast(err?.response?.data?.message ?? "Failed to discard edit request");
    } finally {
      setIsBatchDiscarding(false);
    }
  };

  const renderValue = (key: string, value: any) => {
    if (value === undefined || value === null || value === "") {
      return (
        <Text fontSize="sm" color="gray.400" fontStyle="italic">
          (Empty)
        </Text>
      );
    }

    if (key === "audio") {
      return (
        <Box>
          <audio controls src={String(value)} style={{ width: "100%", height: "36px" }} />
          <Text fontSize="xs" color="blue.500" mt={1}>
            <a href={String(value)} target="_blank" rel="noreferrer">
              Open audio link
            </a>
          </Text>
        </Box>
      );
    }

    if (key === "document") {
      return (
        <Button
          as="a"
          href={String(value)}
          target="_blank"
          rel="noreferrer"
          size="sm"
          colorScheme="blue"
          variant="outline"
        >
          View Document (PDF)
        </Button>
      );
    }

    if (key === "image") {
      return (
        <Image
          src={String(value)}
          alt="Preview"
          maxH="120px"
          objectFit="contain"
          borderRadius="md"
          borderWidth="1px"
          borderColor="gray.200"
        />
      );
    }

    if (key === "videoUrl") {
      return (
        <Text fontSize="sm" color="blue.600" wordBreak="break-all">
          <a href={String(value)} target="_blank" rel="noreferrer">
            {String(value)}
          </a>
        </Text>
      );
    }

    if (key === "meta") {
      const lyrics = getLyricsFromMeta(value);
      if (!lyrics.trim()) {
        return (
          <Text fontSize="sm" color="gray.400" fontStyle="italic">
            (Empty)
          </Text>
        );
      }

      return (
        <Box
          fontSize="sm"
          maxH="150px"
          overflowY="auto"
          p={2}
          bg="white"
          borderRadius="md"
          borderWidth="1px"
          borderColor="gray.200"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(lyrics) }}
        />
      );
    }

    if (key === "description") {
      return (
        <Box
          fontSize="sm"
          maxH="150px"
          overflowY="auto"
          p={2}
          bg="white"
          borderRadius="md"
          borderWidth="1px"
          borderColor="gray.200"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(value)) }}
        />
      );
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return (
          <Text fontSize="sm" color="gray.400" fontStyle="italic">
            (Empty list)
          </Text>
        );
      }
      return (
        <Flex wrap="wrap" gap={1}>
          {value.map((item: any, idx) => {
            const label = typeof item === "object" && item?.name ? item.name : String(item);
            return (
              <Tag key={idx} size="sm" colorScheme="blue">
                {label}
              </Tag>
            );
          })}
        </Flex>
      );
    }

    if (typeof value === "object") {
      return (
        <Text fontSize="sm" fontFamily="monospace">
          {JSON.stringify(value, null, 2)}
        </Text>
      );
    }

    return <Text fontSize="sm">{String(value)}</Text>;
  };

  const getCurrentComposerValue = () => {
    const currentScript = script as any;
    const composerId = getObjectId(script?.composerId);
    return firstNonEmpty(
      getObjectName(currentScript?.scriptComposer),
      getObjectName(currentScript?.composer),
      script?.composer,
      composerId ? composerNameById[composerId] ?? "Selected composer" : undefined,
    );
  };

  const getProposedComposerValue = () => {
    const proposedComposerId = getObjectId(proposedChanges.composerId);
    if (proposedComposerId) {
      return firstNonEmpty(
        composerNameById[proposedComposerId],
        getObjectName(proposedChanges.composer),
        proposedChanges.composer,
        "Selected composer",
      );
    }

    return firstNonEmpty(getObjectName(proposedChanges.composer), proposedChanges.composer);
  };

  const getCurrentCategoryValue = () => {
    const linkedCategoryNames =
      script?.categories?.map((category) => category.name).filter(Boolean) ?? [];
    const idCategoryNames = renderResolvedIds(
      normalizeIds(script?.categoryIds),
      categoryNameById,
      "Selected category",
    );
    const categoryNames = linkedCategoryNames.length > 0 ? linkedCategoryNames : idCategoryNames;

    if (categoryNames.length > 0) return categoryNames;
    if (script?.customCategory) return script.customCategory;

    return script?.category;
  };

  const getProposedCategoryValue = () => {
    const categoryIds = normalizeIds(proposedChanges.categoryIds);
    const categoryNames = renderResolvedIds(categoryIds, categoryNameById, "Selected category");
    const values = [
      ...categoryNames,
      proposedChanges.customCategory,
      categoryIds.length === 0 ? proposedChanges.category : undefined,
    ].filter(Boolean);

    return values.length > 0 ? values : undefined;
  };

  const getCurrentLanguageValue = () => {
    const linkedLanguageNames =
      script?.languages?.map((language) => language.name).filter(Boolean) ?? [];
    const idLanguageNames = renderResolvedIds(
      normalizeIds(script?.languageIds),
      languageNameById,
      "Selected language",
    );
    const languageNames = linkedLanguageNames.length > 0 ? linkedLanguageNames : idLanguageNames;

    if (languageNames.length > 0) return languageNames;

    return splitLegacyList(script?.language);
  };

  const getProposedLanguageValue = () => {
    const languageIds = normalizeIds(proposedChanges.languageIds);
    const languageNames = renderResolvedIds(languageIds, languageNameById, "Selected language");
    const values = [
      ...languageNames,
      languageIds.length === 0 ? proposedChanges.language : undefined,
    ].filter(Boolean);

    return values.length > 0 ? values : undefined;
  };

  const detailScript = requestDetail?.script;
  const hasScript = Boolean(detailScript ?? scriptFallback);
  const script: ScriptDisplayValue | undefined = hasScript
    ? {
        ...scriptFallback,
        ...detailScript,
        categories: detailScript?.categories?.length
          ? detailScript.categories
          : scriptFallback?.categories,
        categoryIds: detailScript?.categoryIds?.length
          ? detailScript.categoryIds
          : scriptFallback?.categoryIds,
        languages: detailScript?.languages?.length
          ? detailScript.languages
          : scriptFallback?.languages,
        languageIds: detailScript?.languageIds?.length
          ? detailScript.languageIds
          : scriptFallback?.languageIds,
        scriptComposer:
          (detailScript as any)?.scriptComposer ?? (scriptFallback as any)?.scriptComposer,
        user: detailScript?.user ?? scriptFallback?.user,
      }
    : undefined;
  const proposedChanges = requestDetail?.proposedChanges ?? {};
  const groups = getEditRequestFieldGroups(proposedChanges);
  const resolvedFields = requestDetail?.resolvedFields ?? {};
  const isPending = requestDetail?.status === "pending";

  const totalGroups = groups.length;
  const resolvedCount = groups.filter((g) => Boolean(resolvedFields[g.key])).length;
  const hasUnresolvedGroups = totalGroups > 0 && resolvedCount < totalGroups;
  const isAnyLoading = Boolean(loadingFieldKey) || isBatchApproving || isBatchDiscarding;
  const getGroupDisplayRows = (group: { key: string; label: string; keys: string[] }) => {
    if (group.key === "composer") {
      return [
        {
          key: "composer",
          label: "Composer",
          currentValue: getCurrentComposerValue(),
          proposedValue: getProposedComposerValue(),
        },
      ];
    }

    if (group.key === "category") {
      return [
        {
          key: "category",
          label: "Category",
          currentValue: getCurrentCategoryValue(),
          proposedValue: getProposedCategoryValue(),
        },
      ];
    }

    if (group.key === "language") {
      return [
        {
          key: "language",
          label: "Language",
          currentValue: getCurrentLanguageValue(),
          proposedValue: getProposedLanguageValue(),
        },
      ];
    }

    return group.keys.map((key) => ({
      key,
      label: EDIT_REQUEST_FIELD_LABELS[key] ?? key,
      currentValue: (script as any)?.[key],
      proposedValue: proposedChanges[key],
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent maxW={{ base: "95%", md: "850px" }}>
        <ModalHeader borderBottom="1px solid" borderColor="gray.100" pb={3}>
          <Flex justify="space-between" align="center" pr={6}>
            <Box>
              <Text fontSize="lg" fontWeight="bold" color="#2b3b4c">
                Review Edit Request
              </Text>
              {script?.name && (
                <Text fontSize="sm" fontWeight="normal" color="gray.500">
                  Script: <strong>{script.name}</strong>
                </Text>
              )}
            </Box>
            {requestDetail?.status && (
              <Badge
                colorScheme={
                  requestDetail.status === "approved"
                    ? "green"
                    : requestDetail.status === "discarded"
                    ? "red"
                    : "yellow"
                }
                fontSize="sm"
                px={3}
                py={1}
                borderRadius="full"
                textTransform="capitalize"
              >
                {requestDetail.status}
              </Badge>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={4}>
          {isLoading ? (
            <Flex justify="center" align="center" minH="250px" direction="column" gap={3}>
              <Spinner size="xl" color="blue.500" />
              <Text color="gray.500">Loading request details...</Text>
            </Flex>
          ) : !requestDetail ? (
            <Box textAlign="center" py={8} color="gray.500">
              Failed to load edit request.
            </Box>
          ) : (
            <Box>
              <Flex
                mb={4}
                p={3}
                bg="#f8fafc"
                borderRadius="md"
                borderWidth="1px"
                borderColor="#e2e8f0"
                justify="space-between"
                wrap="wrap"
                gap={2}
                fontSize="xs"
                color="gray.600"
              >
                <Text>
                  <strong>Requested by:</strong>{" "}
                  {requestDetail.user?.userName ??
                    requestDetail.user?.email ??
                    (requestDetail as any).requestedBy ??
                    "Script Owner"}
                </Text>
                <Text>
                  <strong>Submitted on:</strong>{" "}
                  {dayjs(requestDetail.createdAt).format("MMM D, YYYY h:mm A")}
                </Text>
                {requestDetail.reviewedAt && (
                  <Text>
                    <strong>Reviewed on:</strong>{" "}
                    {dayjs(requestDetail.reviewedAt).format("MMM D, YYYY h:mm A")}
                  </Text>
                )}
              </Flex>

              {groups.length === 0 ? (
                <Box textAlign="center" py={6} color="gray.500">
                  No field modifications found in this edit request.
                </Box>
              ) : (
                <Box>
                  <Flex justify="space-between" align="center" mb={3}>
                    <Text fontSize="xs" color="gray.500">
                      Showing {groups.length} modified{" "}
                      {groups.length === 1 ? "field group" : "field groups"} staged for review.
                    </Text>
                    {totalGroups > 0 && (
                      <Badge
                        colorScheme={resolvedCount === totalGroups ? "green" : "purple"}
                        borderRadius="full"
                        px={2}
                        fontSize="xs"
                      >
                        {resolvedCount}/{totalGroups} resolved
                      </Badge>
                    )}
                  </Flex>

                  {groups.map((group) => {
                    const groupResolution = resolvedFields[group.key];
                    const isGroupResolved = Boolean(groupResolution);
                    const displayRows = getGroupDisplayRows(group);
                    const hasMultipleRows = displayRows.length > 1;

                    return (
                      <Box
                        key={group.key}
                        mb={4}
                        p={3}
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor={
                          groupResolution === "approved"
                            ? "green.200"
                            : groupResolution === "cancelled" || groupResolution === "discarded"
                            ? "red.200"
                            : "gray.200"
                        }
                        bg={
                          groupResolution === "approved"
                            ? "#fbfdfb"
                            : groupResolution === "cancelled" || groupResolution === "discarded"
                            ? "#fdfbfb"
                            : "white"
                        }
                      >
                        <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
                          <Flex align="center" gap={2}>
                            <Text fontWeight="semibold" fontSize="sm" color="#4e6e97">
                              {group.label}
                            </Text>
                            {groupResolution === "approved" && (
                              <Badge
                                colorScheme="green"
                                px={2}
                                py={0.5}
                                borderRadius="full"
                                fontSize="2xs"
                              >
                                Approved
                              </Badge>
                            )}
                            {(groupResolution === "cancelled" ||
                              groupResolution === "discarded") && (
                              <Badge
                                colorScheme="red"
                                px={2}
                                py={0.5}
                                borderRadius="full"
                                fontSize="2xs"
                              >
                                Cancelled
                              </Badge>
                            )}
                          </Flex>

                          {!isGroupResolved && isPending && (
                            <Flex gap={2} align="center">
                              <Button
                                size="xs"
                                colorScheme="red"
                                variant="outline"
                                isLoading={loadingFieldKey === `discard_${group.key}`}
                                isDisabled={isAnyLoading}
                                onClick={() => {
                                  void handleDiscardField(group.key);
                                }}
                              >
                                Discard
                              </Button>
                              <Button
                                size="xs"
                                colorScheme="green"
                                bg="#2E7D32"
                                _hover={{ bg: "#1B5E20" }}
                                color="white"
                                isLoading={loadingFieldKey === `approve_${group.key}`}
                                isDisabled={isAnyLoading}
                                onClick={() => {
                                  void handleApproveField(group.key);
                                }}
                              >
                                Approve
                              </Button>
                            </Flex>
                          )}
                        </Flex>

                        {displayRows.map((row) => {
                          return (
                            <Box key={row.key} mt={hasMultipleRows ? 2 : 0}>
                              {hasMultipleRows && (
                                <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
                                  {row.label}
                                </Text>
                              )}
                              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={3}>
                                <Box
                                  p={3}
                                  bg="#fff5f5"
                                  borderRadius="md"
                                  borderWidth="1px"
                                  borderColor="#feb2b2"
                                >
                                  <Text
                                    fontSize="xs"
                                    fontWeight="bold"
                                    color="red.600"
                                    mb={2}
                                    textTransform="uppercase"
                                  >
                                    Current Live Value
                                  </Text>
                                  {renderValue(row.key, row.currentValue)}
                                </Box>

                                <Box
                                  p={3}
                                  bg="#f0fff4"
                                  borderRadius="md"
                                  borderWidth="1px"
                                  borderColor="#9ae6b4"
                                >
                                  <Text
                                    fontSize="xs"
                                    fontWeight="bold"
                                    color="green.700"
                                    mb={2}
                                    textTransform="uppercase"
                                  >
                                    Proposed Change
                                  </Text>
                                  {renderValue(row.key, row.proposedValue)}
                                </Box>
                              </Grid>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </ModalBody>

        <Divider />
        <ModalFooter justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Button
            colorScheme="red"
            variant="outline"
            onClick={() => {
              void handleDiscardAll();
            }}
            isLoading={isBatchDiscarding}
            isDisabled={!isPending || !hasUnresolvedGroups || isAnyLoading}
          >
            Discard All Remaining
          </Button>

          <Flex align="center" gap={3}>
            {totalGroups > 0 && (
              <Text fontSize="xs" color="gray.500">
                {resolvedCount} of {totalGroups} fields resolved
              </Text>
            )}
            <Button variant="ghost" onClick={onClose} isDisabled={isAnyLoading}>
              Close
            </Button>
            <Button
              colorScheme="green"
              bg="#2E7D32"
              _hover={{ bg: "#1B5E20" }}
              color="white"
              onClick={() => {
                void handleApproveAll();
              }}
              isLoading={isBatchApproving}
              isDisabled={!isPending || !hasUnresolvedGroups || isAnyLoading}
            >
              Approve All Remaining
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditRequestDiffModal;
